
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../../../lib/supabase/client';
import { createNotification } from '../../../lib/notifications/createNotification';
import { resolveZustaendig, matchtZustaendigkeitsFilter, type ZustaendigTyp } from '../../../lib/rechnungen/zustaendigkeit';
import {
  Archive, CalendarCheck, ChatCircleDots, CheckCircle, Clock, Fire, FlowArrow, Funnel, ListChecks, MagnifyingGlass, NotePencil, SpinnerGap, Ticket as TicketIcon,
  Trash, User, UserMinus, Users, WarningCircle, Wrench, X,
} from '@phosphor-icons/react';
import TicketKanban from './TicketKanban';
import VorgaengeAnsicht from './vorgaenge/VorgaengeAnsicht';
import type { ListenAnsicht } from './liste/ListenKopf';
import TicketFormular from './TicketFormular';
import TicketDetailSeite from './TicketDetailSeite';
import { ExportColumn } from '../../../lib/export/types';
import { STATUS_PILL } from '../../../components/ui/statusStyles';
import { ticketPillenSteuerung, type TicketSteuerungProps } from './ticketPillenSteuerung';
import { usePillenSteuerung } from '../../../components/island/widgets/SteuerungsAnsicht';
import { useIslandActions } from '../../../components/island/IslandProvider';
import ChecklistenVorlagenVerwaltung from './ChecklistenVorlagenVerwaltung';
import VorgangsVorlagenVerwaltung from './VorgangsVorlagenVerwaltung';
import { istUeberfaellig, istHeuteFaellig, hatWiedervorlage, schlummert } from '../../../lib/tickets/arbeitskoerbe';
import { brauchtAbschlussGrund, setzeTicketStatus } from '../../../lib/tickets/status';
import { tabellenSpalten } from '../../../lib/tickets/darstellung';
import { useMobilModus } from '../../../lib/immobilien/mobil';
import { SchwebenderToast, useToastPill } from '../../../components/immobilien/ui';
import { GlasTooltipSchicht } from '../../../components/ui/GlasTooltip';
import { useViewport } from '../../../components/island/hooks';
import { ListenKopf } from './liste/ListenKopf';
import { FilterPanel } from './liste/FilterPanel';
import { AuswahlLeiste, Seitenwahl, TicketKarten, TicketTabelle } from './liste/TicketTabelle';
import { KennzahlKachel, KorbChip } from './liste/teile';
import { AbschlussDialog } from './AbschlussDialog';
import { baueExternKarte, externOptionen, passtExtern, type ExternKarte } from '../../../lib/tickets/externFilter';

interface Ticket {
  id: string;
  ticket_nummer: string;
  titel: string;
  beschreibung: string | null;
  status: string;
  prioritaet: string;
  kategorie_id: string | null;
  objekt_id: string | null;
  mieter_id: string | null;
  dienstleister_id?: string | null;
  erstellt_von: string | null;
  zustaendig_id: string | null;
  zustaendig_typ?: 'mitarbeiter' | 'team' | null;
  faellig_am: string | null;
  wiedervorlage_am: string | null;
  wiedervorlage_notiz?: string | null;
  eskaliert: boolean;
  eskaliert_am?: string | null;
  abschluss_grund?: string | null;
  verknuepft_mit?: string | null;
  geloescht_am?: string | null;
  created_at: string;
  updated_at: string;
  // Joined
  kategorie?: { id: string; name: string; farbe: string; parent_id: string | null };
  objekt?: { id: string; bezeichnung: string; strasse: string; hausnummer: string };
  // Clientseitig aufgelöst (kein FK-Embed mehr — zustaendig_id polymorph profiles/teams).
  zustaendig?: { id: string; full_name: string; avatar_url: string | null; isTeam?: boolean } | null;
  mieter_rel?: { id: string; vorname: string; nachname: string };
}

interface Kategorie {
  id: string;
  name: string;
  parent_id: string | null;
  farbe: string;
}

interface TicketListeProps {
  userProfile?: any;
  onNavigateToObjekt?: (id: string) => void;
  onNavigateToMieter?: (id: string) => void;
  /** Sprung in den Kalender auf einen bestimmten Termin (Ticket → Termin). */
  onNavigateToTermin?: (id: string) => void;
}

export type QuickFilter =
  | 'alle' | 'meine' | 'mein_team' | 'ohne_zustaendigkeit' | 'hohe_prio' | 'ueberfaellig' | 'papierkorb'
  | 'heute_faellig' | 'warte_mieter' | 'warte_dl' | 'erledigt' | 'geschlossen' | 'wiedervorlagen' | 'entwuerfe';

// STATUS_PILL (components/ui/statusStyles.ts) ist die EINE Quelle für Status-Labels/-Farben.
// STATUS_CONFIG wird daraus abgeleitet (nur Anzeige-Struktur mit getrenntem bg/color statt klasse).
const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string }> = Object.keys(STATUS_PILL).reduce((acc, key) => {
  const [bg, color] = STATUS_PILL[key].klasse.split(' ');
  acc[key] = { label: STATUS_PILL[key].label, bg, color };
  return acc;
}, {} as Record<string, { label: string; color: string; bg: string }>);

const PRIO_CONFIG: Record<string, { label: string; color: string; icon: string }> = {
  'kritisch': { label: 'Kritisch', color: 'text-red-600', icon: '🔴' },
  'hoch': { label: 'Hoch', color: 'text-orange-500', icon: '🟠' },
  'mittel': { label: 'Mittel', color: 'text-amber-500', icon: '🟡' },
  'niedrig': { label: 'Niedrig', color: 'text-gray-400', icon: '⚪' },
};

const formatDate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

// Tabellen-Spaltenköpfe der Ticket-Liste: label + zugehöriger Sort-Key (handleSort/sortKey).
const TABLE_COLUMNS: { key: 'titel' | 'objekt' | 'zustaendig' | 'prioritaet' | 'status' | 'faellig_am' | 'created_at'; label: string }[] = [
  { key: 'titel', label: 'Ticket' },
  { key: 'objekt', label: 'Objekt' },
  { key: 'zustaendig', label: 'Zuständig' },
  { key: 'prioritaet', label: 'Priorität' },
  { key: 'status', label: 'Status' },
  { key: 'faellig_am', label: 'Fällig' },
  { key: 'created_at', label: 'Erstellt' },
];

// Kompakte Seiten-Liste für die Pagination-Fußzeile: 1 … n mit Nachbarn der aktuellen Seite.
const getPageNumbers = (aktuelle: number, gesamt: number): (number | '...')[] => {
  if (gesamt <= 7) return Array.from({ length: gesamt }, (_, i) => i + 1);
  const kandidaten = new Set<number>([1, gesamt, aktuelle - 1, aktuelle, aktuelle + 1]);
  const sortiert = Array.from(kandidaten).filter(p => p >= 1 && p <= gesamt).sort((a, b) => a - b);
  const ergebnis: (number | '...')[] = [];
  let vorherige = 0;
  for (const p of sortiert) {
    if (vorherige && p - vorherige > 1) ergebnis.push('...');
    ergebnis.push(p);
    vorherige = p;
  }
  return ergebnis;
};

const TICKET_EXPORT_COLUMNS: ExportColumn[] = [
  { key: 'ticket_nummer', label: 'Ticket-Nr.' },
  { key: 'titel', label: 'Titel' },
  { key: 'kategorie', label: 'Kategorie', format: (_, row) => row.kategorie?.name || '' },
  { key: 'prioritaet', label: 'Priorität', format: (v) => PRIO_CONFIG[v]?.label || v || '' },
  { key: 'status', label: 'Status', format: (v) => STATUS_CONFIG[v]?.label || v || '' },
  { key: 'objekt', label: 'Objekt', format: (_, row) => row.objekt?.bezeichnung || '' },
  { key: 'mieter', label: 'Mieter', format: (_, row) => row.mieter_rel ? `${row.mieter_rel.vorname || ''} ${row.mieter_rel.nachname || ''}`.trim() : '' },
  { key: 'zustaendig', label: 'Zuständig', format: (_, row) => row.zustaendig?.full_name || '' },
  { key: 'created_at', label: 'Erstellt am', format: (v) => formatDate(v) },
  { key: 'faellig_am', label: 'Fällig am', format: (v) => formatDate(v) },
];

const TicketListe: React.FC<TicketListeProps> = ({
  userProfile, onNavigateToObjekt, onNavigateToMieter, onNavigateToTermin
}) => {
  const navigate = useNavigate();
  const { id: routeTicketId } = useParams<{ id: string }>();
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [kategorien, setKategorien] = useState<Kategorie[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Saved filters from localStorage
  const savedFilters = (() => {
    try {
      const saved = localStorage.getItem('ticket_filters');
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  })();

  const [viewMode, setViewMode] = useState<ListenAnsicht>(savedFilters?.viewMode || 'liste');
  const [filterStatus, setFilterStatus] = useState<string>(savedFilters?.status || 'alle');
  const [filterPrio, setFilterPrio] = useState<string>(savedFilters?.prio || 'alle');
  const [filterKategorie, setFilterKategorie] = useState<string>(savedFilters?.kategorie || 'alle');
  const [filterZustaendig, setFilterZustaendig] = useState<string>(savedFilters?.zustaendig || 'alle');
  const [filterObjekt, setFilterObjekt] = useState<string>(savedFilters?.objekt || 'alle');
  // Hausmeister-Freigabe / beauftragter Dienstleister (lib/tickets/externFilter.ts)
  const [filterExtern, setFilterExtern] = useState<string>(savedFilters?.extern || 'alle');
  const [externKarte, setExternKarte] = useState<ExternKarte>(new Map());
  const [quickFilter, setQuickFilter] = useState<QuickFilter>(
    savedFilters?.quickFilter || (userProfile?.role === 'team' ? 'mein_team' : 'alle')
  );
  const [filterOffen, setFilterOffen] = useState(false);
  const [laedtNeu, setLaedtNeu] = useState(false);
  // Kanban-Ziehen auf Erledigt/Geschlossen → Abschlussgrund abfragen (wie die Detailseite)
  const [abschluss, setAbschluss] = useState<{ ticket: Ticket; ziel: string } | null>(null);
  const [abschlussLaeuft, setAbschlussLaeuft] = useState(false);
  const [toast, zeigeToast] = useToastPill();
  const { mobil } = useMobilModus();
  const viewport = useViewport();

  // Sortierung
  type SortKey = 'ticket_nummer' | 'titel' | 'status' | 'prioritaet' | 'kategorie' | 'objekt' | 'zustaendig' | 'created_at' | 'faellig_am';
  const [sortKey, setSortKey] = useState<SortKey>(savedFilters?.sortKey || 'created_at');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>(savedFilters?.sortDir || 'desc');

  // Tabellen-Liste: Auswahl (Checkboxen + Bulk), Zeilen-Menü, Pagination
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [seite, setSeite] = useState(1);
  const [proSeite, setProSeite] = useState(20);

  // Filter & Sort in localStorage speichern
  useEffect(() => {
    localStorage.setItem('ticket_filters', JSON.stringify({
      status: filterStatus,
      prio: filterPrio,
      kategorie: filterKategorie,
      zustaendig: filterZustaendig,
      objekt: filterObjekt,
      extern: filterExtern,
      viewMode,
      sortKey,
      sortDir,
      quickFilter,
    }));
  }, [filterStatus, filterPrio, filterKategorie, filterZustaendig, filterObjekt, filterExtern, viewMode, sortKey, sortDir, quickFilter]);

  // Seite zurücksetzen + Auswahl leeren bei Filter-/Korb-/Suchwechsel
  useEffect(() => {
    setSeite(1);
    setSelectedIds(new Set());
  }, [filterStatus, filterPrio, filterKategorie, filterZustaendig, filterObjekt, filterExtern, quickFilter, searchTerm]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const resetFilters = () => {
    setFilterStatus('alle');
    setFilterPrio('alle');
    setFilterKategorie('alle');
    setFilterZustaendig('alle');
    setFilterObjekt('alle');
    setFilterExtern('alle');
    setSearchTerm('');
    setSortKey('created_at');
    setSortDir('desc');
    setQuickFilter('alle');
  };

  const hasActiveFilters = filterStatus !== 'alle' || filterPrio !== 'alle' || filterKategorie !== 'alle' || filterZustaendig !== 'alle' || filterObjekt !== 'alle' || filterExtern !== 'alle' || searchTerm !== '' || quickFilter !== 'alle';

  const [teamMembers, setTeamMembers] = useState<{ id: string; full_name: string }[]>([]);
  const [teams, setTeams] = useState<{ id: string; name: string }[]>([]);
  const [entwurfTicketIds, setEntwurfTicketIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    fetchTickets();
    fetchKategorien();
    fetchTeamMembers();
    fetchEntwurfTicketIds();
  }, []);

  // URL ist die Wahrheitsquelle fuer das offene Detail: selectedTicket aus dem
  // Route-Param :id ableiten — oeffnen, schliessen, Refresh-Sync, ungueltige id.
  useEffect(() => {
    if (!routeTicketId) { setSelectedTicket(null); return; }
    if (tickets.length === 0) return; // auf Laden warten
    const ticket = tickets.find(t => t.id === routeTicketId);
    if (ticket) {
      setSelectedTicket(ticket);
    } else {
      navigate('/admin/tickets', { replace: true });
    }
  }, [routeTicketId, tickets]);

  const fetchTickets = async () => {
    try {
      const { data, error } = await supabase
        .from('tickets')
        .select(`
          *,
          kategorie:ticket_kategorien!tickets_kategorie_id_fkey(id, name, farbe, parent_id),
          objekt:objekte(id, bezeichnung, strasse, hausnummer),
          mieter_rel:mieter(id, vorname, nachname),
          eigentuemer_rel:eigentuemer(id, vorname, nachname, firma_name)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      const allTickets = data || [];
      await checkEskalation(allTickets);
      setTickets(allTickets);
      fetchEntwurfTicketIds();
      void ladeExtern(allTickets);
    } catch (err) {
      console.error('Error fetching tickets:', err);
    } finally {
      setLoading(false);
    }
  };

  // Wer hat das Ticket außer Haus? Hausmeister-Freigaben + beauftragte Dienstleister (für den Filter)
  const ladeExtern = async (alle: Ticket[]) => {
    try {
      const { data: freigaben } = await supabase.from('ticket_hausmeister').select('ticket_id, hausmeister_id');
      const fg = (freigaben || []) as { ticket_id: string; hausmeister_id: string }[];
      const hmIds = [...new Set(fg.map(f => f.hausmeister_id))];
      const dlIds = [...new Set(alle.map(t => t.dienstleister_id).filter((x): x is string => !!x))];
      const [hm, dl] = await Promise.all([
        hmIds.length ? supabase.from('profiles').select('id, full_name').in('id', hmIds) : Promise.resolve({ data: [] }),
        dlIds.length ? supabase.from('dienstleister').select('id, name').in('id', dlIds) : Promise.resolve({ data: [] }),
      ]);
      setExternKarte(baueExternKarte(fg,
        new Map(((hm.data || []) as { id: string; full_name: string | null }[]).map(p => [p.id, p.full_name || ''])),
        alle,
        new Map(((dl.data || []) as { id: string; name: string | null }[]).map(d => [d.id, d.name || '']))));
    } catch (e) {
      console.error('Hausmeister/Dienstleister laden fehlgeschlagen:', e);
    }
  };

  const checkEskalation = async (allTickets: Ticket[]) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const closedStatuses = ['erledigt', 'geschlossen'];
    const prioOrder = ['niedrig', 'mittel', 'hoch', 'kritisch'];

    const toEscalate = allTickets.filter(t =>
      t.faellig_am &&
      new Date(t.faellig_am) < today &&
      !t.eskaliert &&
      !closedStatuses.includes(t.status)
    );

    for (const ticket of toEscalate) {
      const currentPrioIdx = prioOrder.indexOf(ticket.prioritaet);
      const newPrio = currentPrioIdx < prioOrder.length - 1 ? prioOrder[currentPrioIdx + 1] : ticket.prioritaet;

      await supabase.from('tickets').update({
        eskaliert: true,
        eskaliert_am: new Date().toISOString(),
        prioritaet: newPrio,
        status: ticket.status === 'neu' ? 'offen' : ticket.status,
      }).eq('id', ticket.id);

      await supabase.from('ticket_historie').insert({
        ticket_id: ticket.id,
        benutzer_id: null,
        aktion: 'eskalation',
        alter_wert: `Priorität: ${ticket.prioritaet}`,
        neuer_wert: `Automatisch eskaliert → ${newPrio}`,
      });

      if (ticket.zustaendig_id && ticket.zustaendig_typ !== 'team') {
        await createNotification({
          benutzerId: ticket.zustaendig_id,
          modul: 'tickets',
          typ: 'eskalation',
          nachricht: `Ticket ${ticket.ticket_nummer} wurde automatisch eskaliert (überfällig)`,
          referenzId: ticket.id,
          referenzTyp: 'ticket',
          linkPfad: '/admin/tickets',
        });
      }

      ticket.eskaliert = true;
      ticket.prioritaet = newPrio;
    }
  };

  const fetchKategorien = async () => {
    const { data } = await supabase.from('ticket_kategorien').select('*').eq('aktiv', true).order('sortierung');
    setKategorien(data || []);
  };

  const fetchTeamMembers = async () => {
    const [{ data: profs }, { data: teamRows }] = await Promise.all([
      supabase.from('profiles').select('id, full_name').in('role', ['admin', 'team', 'super admin']).eq('zuweisbar', true),
      supabase.from('teams').select('id, name').order('name'),
    ]);
    setTeamMembers(profs || []);
    setTeams(teamRows || []);
  };

  // Best-effort: Tickets mit offenen Aktions-Entwürfen (E4-Tie-in) für den Arbeitskorb "Entwürfe".
  const fetchEntwurfTicketIds = async () => {
    try {
      const { data, error } = await supabase.from('ticket_aktionen').select('ticket_id').eq('status', 'entwurf');
      if (error) throw error;
      setEntwurfTicketIds(new Set((data || []).map((row: { ticket_id: string }) => row.ticket_id)));
    } catch (err) {
      console.error('Error fetching Entwurf-Tickets:', err);
      setEntwurfTicketIds(new Set());
    }
  };

  const handleTicketUpdated = () => {
    fetchTickets();
    // selectedTicket wird vom Route-Param-Effekt frisch gehalten (fetchTickets hat dieselben Joins).
  };

  // Kanban: Statuswechsel über dieselbe Stelle wie die Detailseite (lib/tickets/status.ts).
  // Erledigt/Geschlossen ohne Abschlussgrund fragen erst den Grund ab (Handoff §3).
  const kanbanStatusSetzen = async (ticket: Ticket, neuerStatus: string, grund?: string) => {
    try {
      // Benachrichtigung nur an Mitarbeiter (Team-IDs sind keine Benutzer)
      const quelle = { ...ticket, zustaendig_id: ticket.zustaendig_typ === 'team' ? null : ticket.zustaendig_id };
      await setzeTicketStatus(quelle, neuerStatus, { benutzerId: userProfile?.id ?? null, grund });
      zeigeToast(`${ticket.ticket_nummer} → ${STATUS_CONFIG[neuerStatus]?.label || neuerStatus}`);
      fetchTickets();
    } catch (err) {
      console.error('Error changing status via kanban:', err);
      zeigeToast('Status konnte nicht geändert werden.', true);
    }
  };

  const handleKanbanStatusChange = (ticketId: string, newStatus: string) => {
    const ticket = tickets.find(t => t.id === ticketId);
    if (!ticket || ticket.status === newStatus) return;
    if (brauchtAbschlussGrund(newStatus) && !ticket.abschluss_grund) {
      setAbschluss({ ticket, ziel: newStatus });
      return;
    }
    void kanbanStatusSetzen(ticket, newStatus);
  };

  // Filter logic
  const PRIO_ORDER: Record<string, number> = { 'kritisch': 4, 'hoch': 3, 'mittel': 2, 'niedrig': 1 };
  const STATUS_ORDER: Record<string, number> = { 'neu': 1, 'offen': 2, 'in_bearbeitung': 3, 'warten_auf_rueckmeldung': 4, 'warten_auf_dienstleister': 5, 'eskaliert': 6, 'erledigt': 7, 'geschlossen': 8 };

  // 'heute' als YYYY-MM-DD (lokal) für die reinen Arbeitskorb-Prädikate aus lib/tickets/arbeitskoerbe.ts
  const heuteStr = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  })();

  // Geschlossene Tickets sind der "Archiv"-Zustand: überall ausgeblendet, außer man ist explizit
  // im Korb "Geschlossene" oder hat den Status-Filter auf "geschlossen" gesetzt.
  const zeigeGeschlossene = quickFilter === 'geschlossen' || filterStatus === 'geschlossen';

  // Zuständigkeit clientseitig auflösen (Mitarbeiter ODER Team) — kein FK-Embed mehr.
  const angereicherteTickets = useMemo(() => tickets.map((t) => {
    const z = resolveZustaendig(t.zustaendig_id, (t.zustaendig_typ ?? null) as ZustaendigTyp, teamMembers, teams);
    return { ...t, zustaendig: z ? { id: t.zustaendig_id as string, full_name: z.name, avatar_url: null, isTeam: z.isTeam } : null };
  }), [tickets, teamMembers, teams]);

  // "Mein Team": dem User direkt zugewiesene Team-Tickets ODER Tickets von Team-Kollegen.
  const gehoertZuMeinemTeam = (t: Ticket): boolean => {
    if (!t.zustaendig_id) return false;
    if (t.zustaendig_typ === 'team') return userProfile?.team_ids?.includes(t.zustaendig_id) ?? false;
    return userProfile?.team_member_ids?.includes(t.zustaendig_id) ?? false;
  };

  const preFiltered = angereicherteTickets.filter(t => {
    // Papierkorb zeigt ausschließlich archivierte Tickets
    if (quickFilter === 'papierkorb') return !!t.geloescht_am;
    // Archivierte Tickets in allen normalen Ansichten ausblenden
    if (t.geloescht_am) return false;
    // Geschlossene global raus (nur im expliziten Geschlossene-Kontext sichtbar)
    if (t.status === 'geschlossen' && !zeigeGeschlossene) return false;
    // #542: Tickets mit Wiedervorlage in der Zukunft nur unter „Wiedervorlagen“ — außer bei
    // Fristen (überfällig/heute fällig), Entwürfen und während einer Suche.
    if (schlummert(t, heuteStr) && !searchTerm && !['wiedervorlagen', 'ueberfaellig', 'heute_faellig', 'entwuerfe'].includes(quickFilter)) return false;
    switch (quickFilter) {
      case 'meine': return t.zustaendig_id === userProfile?.id;
      case 'mein_team':
        return gehoertZuMeinemTeam(t);
      case 'ohne_zustaendigkeit': return !t.zustaendig_id;
      case 'hohe_prio': return ['hoch', 'kritisch'].includes(t.prioritaet);
      case 'ueberfaellig':
        // istUeberfaellig() aus arbeitskoerbe.ts: reiner Datums-String-Vergleich (< heuteStr), damit
        // heute fällige Tickets NUR in "Heute fällig" zählen und nicht doppelt in "Überfällig" auftauchen.
        return istUeberfaellig(t, heuteStr);
      case 'heute_faellig': return istHeuteFaellig(t, heuteStr);
      case 'warte_mieter': return t.status === 'warten_auf_rueckmeldung';
      case 'warte_dl': return t.status === 'warten_auf_dienstleister';
      case 'erledigt': return t.status === 'erledigt'; // nur erledigt; geschlossene haben eigenen Korb
      case 'geschlossen': return t.status === 'geschlossen';
      case 'wiedervorlagen': return hatWiedervorlage(t);
      case 'entwuerfe': return entwurfTicketIds.has(t.id);
      default: return true;
    }
  });

  const filteredTickets = preFiltered.filter(t => {
    if (filterStatus !== 'alle' && t.status !== filterStatus) return false;
    if (filterPrio !== 'alle' && t.prioritaet !== filterPrio) return false;
    if (filterKategorie !== 'alle' && t.kategorie_id !== filterKategorie) return false;
    if (!matchtZustaendigkeitsFilter(filterZustaendig, t.zustaendig_id, (t.zustaendig_typ ?? null) as ZustaendigTyp)) return false;
    if (filterObjekt !== 'alle' && t.objekt_id !== filterObjekt) return false;
    if (!passtExtern(filterExtern, externKarte.get(t.id))) return false;
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      const mieter = t.mieter_rel ? `${t.mieter_rel.vorname || ''} ${t.mieter_rel.nachname || ''}` : '';
      const objekt = t.objekt ? `${t.objekt.bezeichnung || ''} ${t.objekt.strasse || ''} ${t.objekt.hausnummer || ''}` : '';
      return t.titel.toLowerCase().includes(q) || t.ticket_nummer.toLowerCase().includes(q) || !!t.beschreibung?.toLowerCase().includes(q)
        || objekt.toLowerCase().includes(q) || mieter.toLowerCase().includes(q);
    }
    return true;
  }).sort((a, b) => {
    const dir = sortDir === 'asc' ? 1 : -1;
    switch (sortKey) {
      case 'ticket_nummer': return dir * a.ticket_nummer.localeCompare(b.ticket_nummer);
      case 'titel': return dir * a.titel.localeCompare(b.titel);
      case 'status': return dir * ((STATUS_ORDER[a.status] || 0) - (STATUS_ORDER[b.status] || 0));
      case 'prioritaet': return dir * ((PRIO_ORDER[a.prioritaet] || 0) - (PRIO_ORDER[b.prioritaet] || 0));
      case 'kategorie': return dir * (a.kategorie?.name || '').localeCompare(b.kategorie?.name || '');
      case 'objekt': return dir * (a.objekt?.bezeichnung || '').localeCompare(b.objekt?.bezeichnung || '');
      case 'zustaendig': return dir * (a.zustaendig?.full_name || '').localeCompare(b.zustaendig?.full_name || '');
      case 'created_at': return dir * (new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
      case 'faellig_am': {
        // Nulls immer ans Ende, unabhängig von der Sortierrichtung (dir).
        if (!a.faellig_am && !b.faellig_am) return 0;
        if (!a.faellig_am) return 1;
        if (!b.faellig_am) return -1;
        return dir * (new Date(a.faellig_am).getTime() - new Date(b.faellig_am).getTime());
      }
      default: return 0;
    }
  });

  // Tabellen-Liste: Pagination (client-seitig über die gefilterte+sortierte Liste)
  const totalSeiten = Math.max(1, Math.ceil(filteredTickets.length / proSeite));
  const seiteBegrenzt = Math.min(seite, totalSeiten);
  const pageTickets = filteredTickets.slice((seiteBegrenzt - 1) * proSeite, seiteBegrenzt * proSeite);
  const pageIds = pageTickets.map(t => t.id);
  const allPageSelected = pageIds.length > 0 && pageIds.every(id => selectedIds.has(id));
  const vonEintrag = filteredTickets.length === 0 ? 0 : (seiteBegrenzt - 1) * proSeite + 1;
  const bisEintrag = Math.min(seiteBegrenzt * proSeite, filteredTickets.length);
  const pageNumbers = getPageNumbers(seiteBegrenzt, totalSeiten);

  const toggleSelectRow = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAllOnPage = () => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (allPageSelected) {
        pageIds.forEach(id => next.delete(id));
      } else {
        pageIds.forEach(id => next.add(id));
      }
      return next;
    });
  };

  // Bulk- und Zeilen-⋮-Archivierung/-Wiederherstellung: identisches Update-Shape wie
  // TicketDetailsSidebar.handleArchive/handleRestore (geloescht_am + ticket_historie-Eintrag).
  const archiveTickets = async (ids: string[]) => {
    setBulkBusy(true);
    try {
      let fehlerAnzahl = 0;
      for (const id of ids) {
        const { error } = await supabase.from('tickets').update({ geloescht_am: new Date().toISOString() }).eq('id', id);
        if (error) { console.error('Error archiving ticket', id, error); fehlerAnzahl++; continue; }
        await supabase.from('ticket_historie').insert({
          ticket_id: id,
          benutzer_id: userProfile?.id,
          aktion: 'archiviert',
          neuer_wert: 'Ticket in den Papierkorb verschoben',
        });
      }
      setSelectedIds(new Set());
      await fetchTickets();
      if (selectedTicket && ids.includes(selectedTicket.id)) {
        navigate('/admin/tickets');
      }
      if (fehlerAnzahl > 0) {
        zeigeToast(`${fehlerAnzahl} von ${ids.length} Tickets konnten nicht aktualisiert werden.`, true);
      }
    } catch (err) {
      console.error('Error archiving tickets:', err);
    } finally {
      setBulkBusy(false);
    }
  };

  const restoreTickets = async (ids: string[]) => {
    setBulkBusy(true);
    try {
      let fehlerAnzahl = 0;
      for (const id of ids) {
        const { error } = await supabase.from('tickets').update({ geloescht_am: null }).eq('id', id);
        if (error) { console.error('Error restoring ticket', id, error); fehlerAnzahl++; continue; }
        await supabase.from('ticket_historie').insert({
          ticket_id: id,
          benutzer_id: userProfile?.id,
          aktion: 'wiederhergestellt',
          neuer_wert: 'Ticket aus dem Papierkorb wiederhergestellt',
        });
      }
      setSelectedIds(new Set());
      await fetchTickets();
      if (fehlerAnzahl > 0) {
        zeigeToast(`${fehlerAnzahl} von ${ids.length} Tickets konnten nicht aktualisiert werden.`, true);
      }
    } catch (err) {
      console.error('Error restoring tickets:', err);
    } finally {
      setBulkBusy(false);
    }
  };

  // Aktive (nicht archivierte) Tickets sind die Basis für alle Statistiken
  const aktiveTickets = tickets.filter(t => !t.geloescht_am);
  // Sichtbare = aktiv ohne geschlossene (geschlossene nur im eigenen Korb)
  // #542: schlummernde Wiedervorlagen zählen nur im Korb „Wiedervorlagen“ (und bei Fristen).
  const wacheTickets = aktiveTickets.filter(t => !schlummert(t, heuteStr));
  const sichtbareTickets = wacheTickets.filter(t => t.status !== 'geschlossen');
  const papierkorbCount = tickets.length - aktiveTickets.length;

  // Stats
  const statCounts = {
    gesamt: aktiveTickets.length,
    offen: aktiveTickets.filter(t => ['neu', 'offen'].includes(t.status)).length,
    bearbeitung: aktiveTickets.filter(t => ['in_bearbeitung', 'warten_auf_rueckmeldung', 'warten_auf_dienstleister', 'eskaliert'].includes(t.status)).length,
    erledigt: aktiveTickets.filter(t => ['erledigt', 'geschlossen'].includes(t.status)).length,
  };

  // Counts for left panel
  const quickFilterCounts = {
    alle: sichtbareTickets.length,
    meine: sichtbareTickets.filter(t => t.zustaendig_id === userProfile?.id).length,
    mein_team: sichtbareTickets.filter(gehoertZuMeinemTeam).length,
    ohne_zustaendigkeit: sichtbareTickets.filter(t => !t.zustaendig_id).length,
    hohe_prio: sichtbareTickets.filter(t => ['hoch', 'kritisch'].includes(t.prioritaet)).length,
    ueberfaellig: aktiveTickets.filter(t => istUeberfaellig(t, heuteStr)).length,
    papierkorb: papierkorbCount,
    heute_faellig: aktiveTickets.filter(t => istHeuteFaellig(t, heuteStr)).length,
    warte_mieter: wacheTickets.filter(t => t.status === 'warten_auf_rueckmeldung').length,
    warte_dl: wacheTickets.filter(t => t.status === 'warten_auf_dienstleister').length,
    erledigt: aktiveTickets.filter(t => t.status === 'erledigt').length,
    geschlossen: aktiveTickets.filter(t => t.status === 'geschlossen').length,
    wiedervorlagen: aktiveTickets.filter(t => hatWiedervorlage(t)).length,
    entwuerfe: aktiveTickets.filter(t => entwurfTicketIds.has(t.id)).length,
  };
  const statusCounts = Object.keys(STATUS_CONFIG).reduce((acc, key) => {
    acc[key] = wacheTickets.filter(t => t.status === key).length;
    return acc;
  }, {} as Record<string, number>);
  const prioCounts = Object.keys(PRIO_CONFIG).reduce((acc, key) => {
    acc[key] = wacheTickets.filter(t => t.prioritaet === key).length;
    return acc;
  }, {} as Record<string, number>);

  const fetchAllTickets = async () => {
    const { data } = await supabase
      .from('tickets')
      .select(`
        *,
        kategorie:ticket_kategorien!tickets_kategorie_id_fkey(id, name, farbe, parent_id),
        objekt:objekte(id, bezeichnung, strasse, hausnummer),
        mieter_rel:mieter(id, vorname, nachname),
        eigentuemer_rel:eigentuemer(id, vorname, nachname, firma_name)
      `)
      .is('geloescht_am', null)
      .order('created_at', { ascending: false })
      .limit(10000);
    // Zuständigkeit clientseitig auflösen (kein FK-Embed mehr) — für die „Zuständig"-Export-Spalte.
    return (data || []).map((t: any) => {
      const z = resolveZustaendig(t.zustaendig_id, (t.zustaendig_typ ?? null) as ZustaendigTyp, teamMembers, teams);
      return { ...t, zustaendig: z ? { id: t.zustaendig_id, full_name: z.name, avatar_url: null, isTeam: z.isTeam } : null };
    });
  };

  const ticketStats = {
    items: [
      { label: 'Gesamt', value: statCounts.gesamt },
      { label: 'Offen', value: statCounts.offen },
      { label: 'In Bearbeitung', value: statCounts.bearbeitung },
      { label: 'Erledigt', value: statCounts.erledigt },
    ]
  };

  const hauptKategorien = kategorien.filter(k => k.parent_id === null);

  // Arbeitskörbe-Navigation: 3 Gruppen mit Sektions-Labels
  const KORB_GRUPPEN: { titel: string; items: { key: QuickFilter; label: string; count: number; Icon: typeof WarningCircle }[] }[] = [
    {
      titel: 'Arbeitskörbe',
      items: [
        { key: 'ueberfaellig', label: 'Überfällig', count: quickFilterCounts.ueberfaellig, Icon: WarningCircle },
        { key: 'heute_faellig', label: 'Heute fällig', count: quickFilterCounts.heute_faellig, Icon: Clock },
        { key: 'warte_mieter', label: 'Warte auf Mieter', count: quickFilterCounts.warte_mieter, Icon: ChatCircleDots },
        { key: 'warte_dl', label: 'Warte auf Dienstleister', count: quickFilterCounts.warte_dl, Icon: Wrench },
        { key: 'erledigt', label: 'Erledigt', count: quickFilterCounts.erledigt, Icon: CheckCircle },
        { key: 'geschlossen', label: 'Geschlossene', count: quickFilterCounts.geschlossen, Icon: Archive },
      ],
    },
    {
      titel: 'Mein Bereich',
      items: [
        { key: 'meine', label: 'Meine Tickets', count: quickFilterCounts.meine, Icon: User },
        { key: 'mein_team', label: 'Mein Team', count: quickFilterCounts.mein_team, Icon: Users },
        { key: 'wiedervorlagen', label: 'Wiedervorlagen', count: quickFilterCounts.wiedervorlagen, Icon: CalendarCheck },
        { key: 'entwuerfe', label: 'Entwürfe', count: quickFilterCounts.entwuerfe, Icon: NotePencil },
      ],
    },
    {
      titel: 'Verwaltung',
      items: [
        { key: 'alle', label: 'Alle Tickets', count: quickFilterCounts.alle, Icon: TicketIcon },
        { key: 'ohne_zustaendigkeit', label: 'Ohne Zuständigkeit', count: quickFilterCounts.ohne_zustaendigkeit, Icon: UserMinus },
        { key: 'hohe_prio', label: 'Hohe Priorität', count: quickFilterCounts.hohe_prio, Icon: Fire },
        { key: 'papierkorb', label: 'Papierkorb', count: quickFilterCounts.papierkorb, Icon: Trash },
      ],
    },
  ];
  const ALLE_KOERBE = KORB_GRUPPEN.flatMap(g => g.items);

  // Steuerung für Pille (Desktop) + Mobile-Drawer — eine Quelle der Wahrheit
  const { openForm, closeForm } = useIslandActions();
  // Objekt-Optionen für den Pillen-Filter: nur Objekte, die in Tickets vorkommen (alphabetisch).
  const objektOptionen = Array.from(
    new Map(tickets.filter(t => t.objekt).map(t => [t.objekt!.id, t.objekt!.bezeichnung])).entries(),
  ).map(([id, bezeichnung]) => ({ id, bezeichnung })).sort((a, b) => (a.bezeichnung || '').localeCompare(b.bezeichnung || ''));
  // Neues Ticket in der Pille (wie alle Ticket-Flows) — kategorien/teamMembers sind
  // Closure-Werte zum Klick-Zeitpunkt (openForm-Elemente sind Snapshots).
  const handleNeuesTicket = () => openForm(
    <TicketFormular
      variant="pille"
      kategorien={kategorien}
      teamMembers={teamMembers}
      teams={teams}
      userProfile={userProfile}
      onClose={() => closeForm()}
      onCreated={() => { closeForm(); fetchTickets(); }}
    />,
    'Neues Ticket',
    640,
    { icon: TicketIcon, farbe: 'orange', badge: 'plus', pflichtHinweis: true },
  );
  const steuerungProps: TicketSteuerungProps = {
    searchTerm, setSearchTerm,
    korbGruppen: KORB_GRUPPEN, quickFilter, setQuickFilter,
    statusConfig: STATUS_CONFIG, statusCounts, filterStatus, setFilterStatus,
    prioConfig: PRIO_CONFIG, prioCounts, filterPrio, setFilterPrio,
    hauptKategorien, filterKategorie, setFilterKategorie,
    teamMembers, teams, filterZustaendig, setFilterZustaendig,
    objekte: objektOptionen, filterObjekt, setFilterObjekt,
    stats: ticketStats.items, hasActiveFilters, resetFilters,
    onNewTicket: handleNeuesTicket,
    onChecklistenVorlagen: () => openForm(<ChecklistenVorlagenVerwaltung />, 'Checklisten-Vorlagen', 640,
      { icon: ListChecks, farbe: 'gruenDunkel', badge: 'haken' }),
    onVorgangsVorlagen: () => openForm(<VorgangsVorlagenVerwaltung />, 'Vorgangs-Vorlagen', 720,
      { icon: FlowArrow, farbe: 'violett' }),
  };
  // UNBEDINGT vor dem early-return — enabled-Koordination Liste↔Detail (analog KontakteListe.tsx)
  const zeigeDetail = !!routeTicketId;
  usePillenSteuerung(ticketPillenSteuerung(steuerungProps), [
    searchTerm, quickFilter, filterStatus, filterPrio, filterKategorie, filterZustaendig, filterObjekt,
    tickets, kategorien, teamMembers, teams,
  ], !zeigeDetail);

  // Volle Ansicht — wenn ein Ticket per Route ausgewählt ist, komplette Seite statt Liste rendern.
  // routeTicketId direkt verwenden (nicht auf selectedTicket/tickets warten) — die Seite lädt und
  // validiert das Ticket selbst inkl. onBack() bei ungültiger/gelöschter id.
  if (zeigeDetail) {
    return (
      <TicketDetailSeite
        ticketId={routeTicketId}
        userProfile={userProfile}
        onBack={() => navigate('/admin/tickets')}
        onNavigateToObjekt={onNavigateToObjekt}
        onNavigateToMieter={onNavigateToMieter}
        onNavigateToTermin={onNavigateToTermin}
        onMutated={fetchTickets}
      />
    );
  }

  // Korb umschalten: erneuter Klick → „Alle Tickets“ (Prototyp)
  const korbWaehlen = (k: QuickFilter) => setQuickFilter(prev => (prev === k && k !== 'alle' ? 'alle' : k));
  const neuLaden = async () => { setLaedtNeu(true); try { await fetchTickets(); } finally { setLaedtNeu(false); } };
  const spalten = tabellenSpalten(viewport.width, viewport.height);
  // Kanban gab es bisher nur am Desktop; auf schmalen Schirmen bleibt es bei der Liste
  const kanban = viewport.width >= 1024 && !mobil && viewMode === 'kanban';
  const oeffnen = (id: string) => navigate('/admin/tickets/' + id);
  const imPapierkorb = quickFilter === 'papierkorb';
  const KENNZAHLEN: { key: QuickFilter; label: string; farbe: string; icon: typeof WarningCircle }[] = [
    { key: 'ueberfaellig', label: 'Überfällig', farbe: 'var(--bad)', icon: WarningCircle },
    { key: 'heute_faellig', label: 'Heute fällig', farbe: 'var(--warn)', icon: Clock },
    { key: 'warte_mieter', label: 'Warte auf Mieter', farbe: 'var(--info)', icon: ChatCircleDots },
    { key: 'warte_dl', label: 'Warte auf Dienstleister', farbe: '#8a63d2', icon: Wrench },
  ];
  // Chips = übrige Körbe in der Reihenfolge des Prototyps
  const CHIP_REIHENFOLGE: QuickFilter[] = ['alle', 'meine', 'mein_team', 'wiedervorlagen', 'entwuerfe', 'ohne_zustaendigkeit', 'hohe_prio', 'erledigt', 'geschlossen', 'papierkorb'];
  const korbChips = CHIP_REIHENFOLGE.map(k => ALLE_KOERBE.find(v => v.key === k)).filter((v): v is (typeof ALLE_KOERBE)[number] => !!v);
  const activeViewLabel = ALLE_KOERBE.find(v => v.key === quickFilter)?.label ?? 'Alle Tickets';
  const filterAktiv = filterStatus !== 'alle' || filterPrio !== 'alle' || filterKategorie !== 'alle' || filterZustaendig !== 'alle' || filterObjekt !== 'alle' || filterExtern !== 'alle';

  return (
    <>
      <GlasTooltipSchicht />
      <ListenKopf
        mobil={mobil}
        ansicht={viewMode}
        setAnsicht={setViewMode}
        kanbanMoeglich={viewport.width >= 1024 && !mobil}
        onNeuLaden={() => void neuLaden()}
        laedt={laedtNeu}
        onNeuesTicket={handleNeuesTicket}
        export={{ titel: 'Tickets', spalten: TICKET_EXPORT_COLUMNS, ansicht: filteredTickets, alle: fetchAllTickets, statistik: ticketStats }}
        onToast={zeigeToast}
      />

      <div className="grid" style={{ gap: 12, gridTemplateColumns: mobil && viewport.width < 600 ? 'repeat(2, minmax(0,1fr))' : 'repeat(4, minmax(0,1fr))' }}>
        {KENNZAHLEN.map(k => (
          <KennzahlKachel key={k.key} label={k.label} zahl={quickFilterCounts[k.key]} farbe={k.farbe} icon={k.icon}
            aktiv={quickFilter === k.key} onClick={() => korbWaehlen(k.key)} tip={`${k.label} – Arbeitskorb: zeigt nur die passenden Tickets`} />
        ))}
      </div>

      <nav aria-label="Arbeitskörbe" className="immo-korbleiste flex overflow-x-auto" style={{ gap: 8, margin: '0 -4px', padding: '2px 4px' }}>
        {korbChips.map(k => (
          <KorbChip key={k.key} label={k.label} anzahl={k.count} icon={k.Icon} aktiv={quickFilter === k.key} onClick={() => korbWaehlen(k.key)} />
        ))}
      </nav>

      <div className="flex items-center" style={{ gap: 10 }}>
        <label className="immo-glas flex min-w-0 flex-1 items-center" style={{ height: 46, borderRadius: 23, gap: 10, padding: '0 8px 0 16px' }}
          data-tip="Suche – Nach Ticketnummer, Titel, Objekt oder Mieter suchen">
          <MagnifyingGlass size={17} color="var(--ink3)" aria-hidden />
          <input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Tickets suchen…" aria-label="Tickets suchen"
            className="min-w-0 flex-1" style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 15, color: 'var(--ink)', fontFamily: 'inherit' }} />
          {searchTerm && (
            <button type="button" onClick={() => setSearchTerm('')} aria-label="Suche leeren" data-tip="Suche leeren"
              className="inline-flex items-center justify-center" style={{ width: 30, height: 30, borderRadius: '50%', border: 'none', cursor: 'pointer', background: 'var(--fill2)', color: 'var(--ink2)' }}>
              <X size={12} weight="bold" />
            </button>
          )}
        </label>
        <button type="button" onClick={() => setFilterOffen(v => !v)} aria-expanded={filterOffen} aria-controls="ticket-filter"
          data-tip="Filter – Nach Status, Priorität, Kategorie, Zuständigkeit und Objekt eingrenzen"
          className={`${filterOffen ? '' : 'immo-glas'} relative inline-flex shrink-0 items-center`}
          style={{ height: 46, padding: '0 16px', gap: 8, borderRadius: 23, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 14, fontWeight: 700,
            ...(filterOffen ? { background: 'var(--acc-fill)', color: 'var(--acc-ink)', boxShadow: 'var(--acc-sh)' } : { color: 'var(--ink)' }) }}>
          <Funnel size={16} weight={filterOffen ? 'fill' : 'bold'} />
          {!mobil || viewport.width >= 600 ? 'Filter' : <span className="sr-only">Filter</span>}
          {filterAktiv && <span aria-label="Filter aktiv" style={{ position: 'absolute', top: 8, right: 10, width: 8, height: 8, borderRadius: '50%', background: 'var(--bad)' }} />}
        </button>
      </div>

      {filterOffen && (
        <div id="ticket-filter">
          <FilterPanel
            statusCounts={statusCounts} filterStatus={filterStatus} setFilterStatus={setFilterStatus}
            prioCounts={prioCounts} filterPrio={filterPrio} setFilterPrio={setFilterPrio}
            hauptKategorien={hauptKategorien} filterKategorie={filterKategorie} setFilterKategorie={setFilterKategorie}
            teamMembers={teamMembers} teams={teams} filterZustaendig={filterZustaendig} setFilterZustaendig={setFilterZustaendig}
            objekte={objektOptionen} filterObjekt={filterObjekt} setFilterObjekt={setFilterObjekt}
            externOptionen={externOptionen(externKarte, preFiltered.map(t => t.id))} filterExtern={filterExtern} setFilterExtern={setFilterExtern}
            onChecklistenVorlagen={steuerungProps.onChecklistenVorlagen}
            onVorgangsVorlagen={steuerungProps.onVorgangsVorlagen}
            hasActiveFilters={hasActiveFilters} resetFilters={resetFilters}
          />
        </div>
      )}

      <div className="flex flex-wrap items-center" style={{ gap: 10, minHeight: 32 }}>
        <h2 className="m-0" style={{ fontSize: 20, fontWeight: 800, letterSpacing: '-0.02em' }}>
          {activeViewLabel} <span className="tabular-nums" style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink3)' }}>({filteredTickets.length})</span>
        </h2>
        {hasActiveFilters && <span style={{ height: 22, padding: '0 9px', borderRadius: 11, fontSize: 11, fontWeight: 800, lineHeight: '22px', background: 'var(--fill2)', color: 'var(--ink2)' }}>gefiltert</span>}
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center" style={{ padding: '80px 0', gap: 12, color: 'var(--ink3)' }}>
          <SpinnerGap size={32} className="animate-spin" aria-hidden />
          <span style={{ fontSize: 13, fontWeight: 600 }}>Tickets werden geladen...</span>
        </div>
      ) : viewMode === 'vorgaenge' ? (
        <VorgaengeAnsicht tickets={filteredTickets} onOeffnen={(t) => oeffnen(t.id)} />
      ) : kanban ? (
        <TicketKanban tickets={filteredTickets} onSelectTicket={(t) => oeffnen(t.id)} onStatusChange={handleKanbanStatusChange} selectedTicketId={selectedTicket?.id} />
      ) : spalten.tabelle ? (
        <div className="flex flex-col" style={{ gap: 10 }}>
          {selectedIds.size > 0 && (
            <AuswahlLeiste anzahl={selectedIds.size} papierkorb={imPapierkorb} laeuft={bulkBusy}
              onAktion={() => void (imPapierkorb ? restoreTickets(Array.from(selectedIds)) : archiveTickets(Array.from(selectedIds)))}
              onAufheben={() => setSelectedIds(new Set())} />
          )}
          <TicketTabelle
            tickets={pageTickets} heute={heuteStr} spalten={spalten}
            sortKey={sortKey} sortDir={sortDir} onSort={handleSort}
            ausgewaehlt={selectedIds} onAuswahl={toggleSelectRow} alleAufSeite={allPageSelected} onAlleAufSeite={toggleSelectAllOnPage}
            papierkorb={imPapierkorb} entwurfIds={entwurfTicketIds} onOeffnen={oeffnen}
            onArchiv={id => void (imPapierkorb ? restoreTickets([id]) : archiveTickets([id]))}
          />
          {filteredTickets.length > 0 && (
            <Seitenwahl von={vonEintrag} bis={bisEintrag} gesamt={filteredTickets.length} seite={seiteBegrenzt} seiten={totalSeiten}
              nummern={pageNumbers} onSeite={setSeite} proSeite={proSeite} onProSeite={n => { setProSeite(n); setSeite(1); }} />
          )}
        </div>
      ) : (
        <TicketKarten tickets={filteredTickets} heute={heuteStr} entwurfIds={entwurfTicketIds} onOeffnen={oeffnen} />
      )}

      {abschluss && (
        <AbschlussDialog ticketNummer={abschluss.ticket.ticket_nummer} zielStatus={abschluss.ziel} laeuft={abschlussLaeuft}
          onAbbrechen={() => setAbschluss(null)}
          onAbschliessen={async grund => {
            setAbschlussLaeuft(true);
            try { await kanbanStatusSetzen(abschluss.ticket, abschluss.ziel, grund); setAbschluss(null); } finally { setAbschlussLaeuft(false); }
          }} />
      )}
      <SchwebenderToast toast={toast} mobil={mobil} />
    </>
  );
};

export default TicketListe;
